import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import '../styles/LetterToHeavenPostDetailsPC.css';
import { getLetterToHeavenDetail, getFlowerImages, deleteLetterToHeaven } from '../api';
import FamilyPlazaSubHeading from './family-plaza/FamilyPlazaSubHeading';
import FamilyPlazaTopSection from './family-plaza/FamilyPlazaTopSection';

const LetterToHeavenPostDetailsPC = () => {
  const navigate = useNavigate();
  const handleNavigation = (route) => { navigate(route); };
  const { id } = useParams();

  // State for letter data and password verification
  const [letterData, setLetterData] = useState(null);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);
  const [flowerImages, setFlowerImages] = useState({});
  const [storedPassword, setStoredPassword] = useState('');

  // Load letter data from API and get password from localStorage
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch letter details from API
        const [detailRes, imagesRes] = await Promise.all([
          getLetterToHeavenDetail(id),
          getFlowerImages(),
        ]);

        const detail = detailRes.data || {};

        // Set up flower images
        const images = Array.isArray(imagesRes.data) ? imagesRes.data : [];
        const imageMap = {};
        images.forEach(img => { imageMap[img.boardStampId] = img.imageUrl; });
        setFlowerImages(imageMap);

        // Get password from localStorage
        const localData = localStorage.getItem('letterToHeavenDataPC');
        const unifiedData = localStorage.getItem('letterToHeavenData');
        let localRecipient = '';
        let localSender = '';
        let localSelectedStamp = '';
        let password = '';

        // Try unified storage first, then platform-specific storage
        let dataToCheck = null;
        if (unifiedData) {
          try {
            dataToCheck = JSON.parse(unifiedData);
          } catch (e) {
            // Ignore parsing errors
          }
        }

        if (!dataToCheck && localData) {
          try {
            dataToCheck = JSON.parse(localData);
          } catch (e) {
            // Ignore parsing errors
          }
        }

        if (dataToCheck) {
          // Handle both array and single object formats
          const letters = Array.isArray(dataToCheck) ? dataToCheck : [dataToCheck];

          console.log('🔍 Looking for letter with ID:', id);
          console.log('🔍 API letter title:', detail.title);
          console.log('🔍 API letter sender:', detail.userName);
          console.log('🔍 Available letters in localStorage:', letters.map(l => ({ id: l.id, title: l.title, sender: l.sender })));

          // Try to find by ID first
          let matchingLetter = letters.find(l => l.id == id);
          console.log('🔍 Found by ID:', matchingLetter);

          // If not found by ID, try to find by title and sender (for old posts)
          if (!matchingLetter && detail.title && detail.userName) {
            matchingLetter = letters.find(l =>
              l.title === detail.title &&
              l.sender === detail.userName
            );
            console.log('🔍 Found by title + sender:', matchingLetter);
          }

          // If still not found, try by title only
          if (!matchingLetter && detail.title) {
            matchingLetter = letters.find(l => l.title === detail.title);
            console.log('🔍 Found by title only:', matchingLetter);
          }

          if (matchingLetter) {
            localRecipient = matchingLetter.recipient || '';
            localSender = matchingLetter.sender || '';
            password = matchingLetter.password || '';
            if (matchingLetter.selectedStamp) {
              localSelectedStamp = matchingLetter.selectedStamp;
            }
            console.log('🔍 Found password:', password);
            setStoredPassword(password);
          } else {
            console.log('🔍 No matching letter found!');
          }
        }

        // Get recipient from board list if available
        const boardListRecipient = localStorage.getItem('selectedLetterRecipient') || '';

        const normalized = {
          id: detail.id,
          recipient: localRecipient || detail.toName || detail.recipient || detail.to || detail.to_name || detail.recipientName || detail.recipient_name || boardListRecipient || '',
          sender: localSender || detail.userName || detail.sender || '',
          title: detail.title,
          content: detail.content || '',
          date: detail.registDate || detail.date,
          selectedStamp: localSelectedStamp || ((typeof detail.boardStamp !== 'undefined') ? `stamp-${detail.boardStamp}` : (detail.selectedStamp || detail.flowerImageUrl || 'stamp-1')),
        };
        setLetterData(normalized);
      } catch (err) {
        setLetterData(null);
      }
    };
    if (id) fetchData();
  }, [id]);

  // Check password whenever enteredPassword changes
  useEffect(() => {
    if (storedPassword && enteredPassword) {
      setIsPasswordCorrect(enteredPassword === storedPassword);
    } else {
      setIsPasswordCorrect(false);
    }
  }, [enteredPassword, storedPassword]);

  // Handle password input change
  const handlePasswordChange = (password) => {
    setEnteredPassword(password);
  };

  // Action button handlers
  const handleListButton = () => {
    navigate('/letter-to-heaven-board-list');
  };

  // Format date to YYYY.MM.DD format
  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      // If already in YYYY.MM.DD format, return as is
      if (/^\d{4}\.\d{2}\.\d{2}$/.test(dateString)) {
        return dateString;
      }

      // If in YYYY-MM-DD format, convert to YYYY.MM.DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString.replace(/-/g, '.');
      }

      // For other formats, try to parse and format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch (error) {
      return dateString;
    }
  };

  const handleEditButton = () => {
    if (isPasswordCorrect && letterData) {
      // Save the current letter data for editing, including password for pre-fill
      const editingPayload = { ...letterData, password: storedPassword };
      localStorage.setItem('editingLetterToHeavenDataPC', JSON.stringify(editingPayload));
      navigate('/letter-to-heaven-writing');
    }
  };

  const handleDeleteButton = async () => {
    if (isPasswordCorrect && letterData) {
      try {
        // Delete from API with password
        await deleteLetterToHeaven(letterData.id, enteredPassword);
        navigate('/letter-to-heaven-board-list');
      } catch (error) {
        // Fallback to localStorage removal if API fails
        localStorage.removeItem('letterToHeavenDataPC');
        localStorage.removeItem('selectedLetterToHeavenDataPC');
        localStorage.removeItem('editingLetterToHeavenDataPC');
        navigate('/letter-to-heaven-board-list');
      }
    }
  };

  // Get stamp image based on selected stamp (API images only)
  const getStampImage = (selectedStamp) => {
    if (typeof selectedStamp === 'string' && selectedStamp.startsWith('http')) {
      return selectedStamp;
    }
    if (typeof selectedStamp === 'number') {
      return flowerImages[selectedStamp] || flowerImages[selectedStamp + 1] || '';
    }
    if (typeof selectedStamp === 'string' && selectedStamp.startsWith('stamp-')) {
      const num = parseInt(selectedStamp.replace('stamp-', ''), 10);
      return flowerImages[num] || '';
    }
    return '';
  };

  const normalizeContent = (str) => {
    if (!str) return '';
    return str
      .replace(/&nbsp;/g, ' ')
      .replace(/\.{3}/g, '…');
  };

  return (

    <div className="letter-to-heaven-board-list-pc page-letter-to-heaven-board-list-pc announcement-list-page donate-info-page-pc">
      <FamilyPlazaTopSection />


      <main className="main-content">
        <FamilyPlazaSubHeading heading="하늘로 보내는 편지" />


        {/* Main Content */}
        <div className="letter-to-heaven-post-details-main-content">
          <div className="letter-to-heaven-post-details-page-content">
            <div className="letter-to-heaven-post-details-container">

              {/* Content Area - Letter to Heaven Post Details */}
              <div className="letter-to-heaven-post-details-content-area">

                {/* Letter Content */}
                <div className="letter-to-heaven-post-details-letter">
                  {/* Top Left - Recipient */}
                  <div className="letter-to-heaven-post-details-recipient">
                    To. {letterData ? letterData.recipient : ''}
                  </div>

                  {/* Title - Bold and Larger */}
                  <div className="letter-to-heaven-post-details-title">
                    {letterData ? letterData.title : ''}
                  </div>

                  {/* Body Text - Multiple paragraphs */}
                  {/* <div className="letter-to-heaven-post-details-body">
                    {letterData && letterData.content ? (
                      letterData.content.split('\n').map((line, index) => (
                        <p key={index}>{line}</p>
                      ))
                    ) : letterData ? (
                      <p>내용이 없습니다.</p>
                    ) : null}
                  </div> */}

                  <div
                    className="letter-to-heaven-post-details-mobile-letter-content-unique"
                    dangerouslySetInnerHTML={{
                      __html: letterData ? normalizeContent(letterData.content) : ''
                    }}
                  ></div>

                  {/* Bottom Right - Date and Sender */}
                  <div className="letter-to-heaven-post-details-footer">
                    <div className="letter-to-heaven-post-details-date">
                      {letterData ? formatDate(letterData.date) : ''}
                    </div>
                    <div className="letter-to-heaven-post-details-sender">
                      {letterData ? letterData.sender : ''}
                    </div>
                  </div>
                </div>

                {/* Bottom Action Bar */}
                <div className="letter-to-heaven-post-details-action-bar">
                  <button className="letter-to-heaven-post-details-list-btn" onClick={handleListButton}>목록</button>

                  <div className="letter-to-heaven-post-details-password-section">
                    <input
                      type="password"
                      className="letter-to-heaven-post-details-password-input"
                      placeholder="비밀번호"
                      value={enteredPassword}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                    />
                    <div className="letter-to-heaven-post-details-action-buttons">
                      <button
                        className="letter-to-heaven-post-details-edit-btn"
                        onClick={handleEditButton}
                        disabled={!isPasswordCorrect}
                        style={{
                          opacity: isPasswordCorrect ? 1 : 0.5,
                          cursor: isPasswordCorrect ? 'pointer' : 'not-allowed'
                        }}
                      >수정</button>
                      <button
                        className="letter-to-heaven-post-details-delete-btn"
                        onClick={handleDeleteButton}
                        disabled={!isPasswordCorrect}
                        style={{
                          opacity: isPasswordCorrect ? 1 : 0.5,
                          cursor: isPasswordCorrect ? 'pointer' : 'not-allowed'
                        }}
                      >삭제</button>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </div>

      </main>
    </div>
  );
};

export default LetterToHeavenPostDetailsPC; 